import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCompany } from '../../hooks/useCompany';
import { stockAPI, tellerAccountAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Package,
  Plus,
  AlertTriangle,
  ShoppingCart,
  ArrowRightLeft,
  RefreshCw,
  Smartphone,
  CreditCard,
  Signal,
} from 'lucide-react';

const formatCurrency = (value) => {
  return `$${new Intl.NumberFormat('en-US').format(value || 0)}`;
};

const CATEGORY_ICONS = {
  mobile_money: Smartphone,
  scratch_card: CreditCard,
  airtime: CreditCard,
  sim_card: Signal,
};

const CATEGORY_COLORS = {
  mobile_money: 'text-blue-600 bg-blue-500/10',
  scratch_card: 'text-purple-600 bg-purple-500/10',
  airtime: 'text-green-600 bg-green-500/10',
  sim_card: 'text-orange-600 bg-orange-500/10',
};

function CreateItemModal({ isOpen, onClose, onSuccess, companyId }) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('mobile_money');
  const [unitCost, setUnitCost] = useState('0');
  const [unitPrice, setUnitPrice] = useState('0');
  const [reorderLevel, setReorderLevel] = useState('10');
  const [description, setDescription] = useState('');

  const mutation = useMutation({
    mutationFn: (data) => stockAPI.createItem(data),
    onSuccess: () => {
      toast.success('Stock item created successfully');
      onSuccess();
      onClose();
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to create item');
    },
  });

  const resetForm = () => {
    setName('');
    setCategory('mobile_money');
    setUnitCost('0');
    setUnitPrice('0');
    setReorderLevel('10');
    setDescription('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name) {
      toast.error('Please enter item name');
      return;
    }
    mutation.mutate({
      company_id: companyId,
      name,
      category,
      unit_cost: parseFloat(unitCost) || 0,
      unit_price: parseFloat(unitPrice) || 0,
      reorder_level: parseInt(reorderLevel) || 10,
      description,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold mb-4">Add Stock Item</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Item Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., MTN Airtime $10"
              className="w-full px-3 py-2 bg-background border border-border rounded-md"
              data-testid="input-item-name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-md"
              data-testid="select-category"
            >
              <option value="mobile_money">Mobile Money Float</option>
              <option value="scratch_card">Scratch Card</option>
              <option value="airtime">Airtime</option>
              <option value="sim_card">SIM Card</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Unit Cost ($)</label>
              <input
                type="number"
                value={unitCost}
                onChange={(e) => setUnitCost(e.target.value)}
                placeholder="0.00"
                className="w-full px-3 py-2 bg-background border border-border rounded-md"
                data-testid="input-unit-cost"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Unit Price ($)</label>
              <input
                type="number"
                value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value)}
                placeholder="0.00"
                className="w-full px-3 py-2 bg-background border border-border rounded-md"
                data-testid="input-unit-price"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Reorder Level</label>
            <input
              type="number"
              value={reorderLevel}
              onChange={(e) => setReorderLevel(e.target.value)}
              placeholder="10"
              className="w-full px-3 py-2 bg-background border border-border rounded-md"
              data-testid="input-reorder-level"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              rows={2}
              className="w-full px-3 py-2 bg-background border border-border rounded-md"
              data-testid="input-description"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-border rounded-md hover:bg-muted">
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
              data-testid="button-create-item"
            >
              {mutation.isPending ? 'Creating...' : 'Create Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StockTransactionModal({ item, isOpen, onClose, onSuccess }) {
  const [transactionType, setTransactionType] = useState('purchase');
  const [quantity, setQuantity] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');

  const mutation = useMutation({
    mutationFn: (data) => stockAPI.itemTransaction(item.id, data),
    onSuccess: () => {
      toast.success('Stock transaction completed');
      onSuccess();
      onClose();
      setQuantity('');
      setReference('');
      setNotes('');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Transaction failed');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!quantity || parseInt(quantity) <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    mutation.mutate({
      stock_item_id: item.id,
      transaction_type: transactionType,
      quantity: parseInt(quantity),
      reference,
      notes,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold mb-4">Stock Transaction - {item?.name}</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Transaction Type</label>
            <select
              value={transactionType}
              onChange={(e) => setTransactionType(e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-md"
              data-testid="select-transaction-type"
            >
              <option value="purchase">Purchase (Add Stock)</option>
              <option value="sale">Sale (Remove Stock)</option>
              <option value="return">Return (Add Back)</option>
              <option value="adjustment">Adjustment (Set Quantity)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Quantity {transactionType !== 'adjustment' && `(Current: ${item?.current_quantity})`}
            </label>
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder={transactionType === 'adjustment' ? 'New quantity' : 'Quantity'}
              className="w-full px-3 py-2 bg-background border border-border rounded-md"
              data-testid="input-quantity"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Reference</label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Invoice/PO number"
              className="w-full px-3 py-2 bg-background border border-border rounded-md"
              data-testid="input-reference"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes"
              rows={2}
              className="w-full px-3 py-2 bg-background border border-border rounded-md"
              data-testid="input-notes"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-border rounded-md hover:bg-muted">
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
              data-testid="button-submit-transaction"
            >
              {mutation.isPending ? 'Processing...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function StockManagement() {
  const { selectedCompany, getDFSCompany } = useCompany();
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);

  const dfsCompany = getDFSCompany ? getDFSCompany() : selectedCompany;
  const companyId = dfsCompany?.id;

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['stock-items', companyId],
    queryFn: () => stockAPI.getItems(companyId).then((res) => res.data),
    enabled: !!companyId,
  });

  const handleRefresh = () => {
    queryClient.invalidateQueries(['stock-items', companyId]);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const lowStockItems = items.filter((i) => i.current_quantity <= i.reorder_level);
  const totalValue = items.reduce((sum, i) => sum + (i.current_quantity * i.unit_cost), 0);

  return (
    <div className="space-y-6" data-testid="stock-management-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Stock Management</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage inventory for mobile money, airtime, and SIM cards
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-3 py-2 border border-border rounded-md hover:bg-muted"
            data-testid="button-refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
            data-testid="button-add-item"
          >
            <Plus className="h-4 w-4" />
            Add Item
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Package className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Total Items</span>
          </div>
          <p className="text-2xl font-semibold">{items.length}</p>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Total Stock Value</span>
          </div>
          <p className="text-2xl font-semibold text-green-600">{formatCurrency(totalValue)}</p>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <span className="text-sm text-muted-foreground">Low Stock Alerts</span>
          </div>
          <p className="text-2xl font-semibold text-yellow-600">{lowStockItems.length}</p>
        </div>
      </div>

      {/* Low Stock Alert */}
      {lowStockItems.length > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 text-yellow-600 mb-2">
            <AlertTriangle className="h-5 w-5" />
            <span className="font-semibold">Low Stock Alert</span>
          </div>
          <p className="text-sm text-yellow-700 dark:text-yellow-400">
            {lowStockItems.length} item{lowStockItems.length > 1 ? 's' : ''} running low:{' '}
            {lowStockItems.map((i) => i.name).join(', ')}
          </p>
        </div>
      )}

      {/* Stock Items Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => {
          const Icon = CATEGORY_ICONS[item.category] || Package;
          const colorClass = CATEGORY_COLORS[item.category] || 'text-gray-600 bg-gray-500/10';
          const isLowStock = item.current_quantity <= item.reorder_level;

          return (
            <div
              key={item.id}
              className={`bg-card border rounded-lg p-4 ${isLowStock ? 'border-yellow-500/50' : 'border-border'}`}
              data-testid={`stock-item-${item.name.toLowerCase().replace(/\s/g, '-')}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${colorClass}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm">{item.name}</h3>
                    <p className="text-xs text-muted-foreground capitalize">
                      {item.category.replace('_', ' ')}
                    </p>
                  </div>
                </div>
                {isLowStock && (
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                <div>
                  <p className="text-muted-foreground">Quantity</p>
                  <p className={`font-semibold ${isLowStock ? 'text-yellow-600' : ''}`}>
                    {item.current_quantity}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Reorder Level</p>
                  <p className="font-semibold">{item.reorder_level}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Unit Cost</p>
                  <p className="font-semibold">{formatCurrency(item.unit_cost)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Unit Price</p>
                  <p className="font-semibold">{formatCurrency(item.unit_price)}</p>
                </div>
              </div>

              <button
                onClick={() => {
                  setSelectedItem(item);
                  setShowTransactionModal(true);
                }}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm border border-border rounded-md hover:bg-muted"
                data-testid={`button-transaction-${item.id}`}
              >
                <ArrowRightLeft className="h-4 w-4" />
                Stock Transaction
              </button>
            </div>
          );
        })}

        {items.length === 0 && (
          <div className="col-span-full text-center py-12 text-muted-foreground">
            No stock items found. Click "Add Item" to create one.
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateItemModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleRefresh}
        companyId={companyId}
      />
      <StockTransactionModal
        item={selectedItem}
        isOpen={showTransactionModal}
        onClose={() => setShowTransactionModal(false)}
        onSuccess={handleRefresh}
      />
    </div>
  );
}
